import React, { useCallback, useEffect, useState } from "react";

import { apiGet, apiPost, apiRequest } from "../../../../Api/Client.js";
import { errorText } from "../../../../Shared/uiStyles.js";
import { Alert, Badge, Button, Card, EmptyState, Field, FormGrid, Input, Page, PageHeader, Select, Table } from "../../../../Ui/index.jsx";
import { fromField, ruleHint, ruleLabel, ruleOptions, toField } from "./payRuleWording.js";

const PAY = "/FinanceAndPayroll";

// Company admin > Pay rules: how this company counts a month's pay, the tax rules for monthly fees, and commission plans.
export function PayRulesView() {
  return (
    <Page>
      <PageHeader title="Pay rules" subtitle="How a month's pay is counted for your company." />
      <RulesCard />
      <TaxRulesCard />
      <PlansCard />
    </Page>
  );
}

function useLoaded(path, pick) {
  const [state, setState] = useState({ value: null, error: "" });
  const load = useCallback(() => apiGet(path)
    .then((out) => setState({ value: pick(out), error: "" }))
    .catch((err) => setState({ value: null, error: errorText(err, "This could not be loaded.") })), [path]);
  useEffect(() => { load(); }, [load]);
  return [state, load];
}

function RulesCard() {
  const [{ value: rules, error }, load] = useLoaded(`${PAY}/pay-rules/`, (out) => out.rules);
  const [draft, setDraft] = useState({});
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (rules) setDraft(Object.fromEntries(rules.map((r) => [r.key, toField(r)]))); }, [rules]);

  const changed = (rules || []).filter((r) => draft[r.key] !== toField(r));
  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      await apiRequest(`${PAY}/pay-rules/`, { method: "PUT", body: { rules: Object.fromEntries(changed.map((r) => [r.key, fromField(r, draft[r.key])])) } });
      setNotice({ tone: "success", text: "Saved." });
      await load();
    } catch (err) {
      setNotice({ tone: "danger", text: errorText(err, "That did not save. Please try again.") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="How pay is counted">
      {error ? <Alert tone="danger">{error}</Alert> : !rules ? <EmptyState>Loading…</EmptyState> : (
        <form className="Pay-Form" onSubmit={save}>
          <FormGrid>
            {rules.map((rule) => {
              const options = ruleOptions(rule);
              return (
                <Field key={rule.key} label={ruleLabel(rule)} hint={ruleHint(rule)}>
                  {options.length ? (
                    <Select value={draft[rule.key] ?? ""} options={options} onChange={(e) => setDraft({ ...draft, [rule.key]: e.target.value })} />
                  ) : (
                    <Input type={rule.kind === "int" ? "number" : "text"} min={rule.kind === "int" ? "0" : undefined} value={draft[rule.key] ?? ""}
                      onChange={(e) => setDraft({ ...draft, [rule.key]: e.target.value })} />
                  )}
                </Field>
              );
            })}
          </FormGrid>
          {notice && <Alert tone={notice.tone} role="status">{notice.text}</Alert>}
          <div className="Modal-Actions">
            <Button type="submit" variant="primary" disabled={busy || changed.length === 0}>{busy ? "Saving…" : "Save"}</Button>
          </div>
        </form>
      )}
    </Card>
  );
}

const percent = (rate) => `${Number((rate * 100).toFixed(2))}%`;

function TaxRulesCard() {
  const [{ value: rules, error }, load] = useLoaded(`${PAY}/pay-tax-rules/`, (out) => out.rules);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (call, text) => {
    setBusy(true);
    setNotice(null);
    try {
      await call();
      setNotice({ tone: "success", text });
      await load();
    } catch (err) {
      setNotice({ tone: "danger", text: errorText(err, "That did not work. Please try again.") });
    } finally {
      setBusy(false);
    }
  };
  const columns = [
    { key: "label", header: "Rule", className: "is-card-title", render: (r) => <span><span className="ds-strong">{r.section}</span> {r.label}</span> },
    { key: "rate", header: "With a PAN", align: "right", render: (r) => percent(r.rate) },
    { key: "no_pan", header: "Without a PAN", align: "right", render: (r) => percent(r.no_pan_rate) },
    { key: "limit", header: "Tax starts after", align: "right", render: (r) => (r.annual_threshold === null ? "Every fee" : `₹${Number(r.annual_threshold).toLocaleString("en-IN")} a year`) },
    { key: "status", header: "Status", render: (r) => (r.verified ? <Badge variant="success">Checked</Badge> : <Badge variant="warning">Needs checking</Badge>) },
    { key: "act", header: "", align: "right", className: "is-card-actions", render: (r) => (r.verified ? null
      : <Button size="sm" disabled={busy} onClick={() => run(() => apiPost(`${PAY}/pay-tax-rules/${r.id}/verify/`, {}), "Marked as checked.")}>Mark as checked</Button>) },
  ];
  return (
    <Card title="Tax on monthly fees"
      actions={<Button size="sm" disabled={busy} onClick={() => run(() => apiPost(`${PAY}/pay-tax-rules/starter/`, {}), "The usual rules are added. Check each one against the current tax rules before using it.")}>Add the usual rules</Button>}>
      <p className="ds-small">A monthly fee is paid with tax withheld. A rule is used only after someone other than its author has checked it against the current tax rules. Where a rule has a yearly limit, tax starts in the month the year's fees pass it, on all the fees of the year so far.</p>
      {notice && <Alert tone={notice.tone} role="status">{notice.text}</Alert>}
      {error ? <Alert tone="danger">{error}</Alert> : !rules ? <EmptyState>Loading…</EmptyState> : (
        <Table stack flush columns={columns} rows={rules} empty="No tax rules yet." />
      )}
    </Card>
  );
}

function PlansCard() {
  const [{ value: plans, error }, load] = useLoaded(`${PAY}/pay-commission-plans/`, (out) => out.plans);
  const [form, setForm] = useState({ name: "", bands: "0:5", cap: "" });
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async (plan) => {
    setBusy(true);
    setNotice(null);
    try {
      await apiPost(`${PAY}/pay-commission-plans/${plan.id}/verify/`, {});
      setNotice({ tone: "success", text: "Marked as checked." });
      await load();
    } catch (err) {
      setNotice({ tone: "danger", text: errorText(err, "That did not work. Please try again.") });
    } finally {
      setBusy(false);
    }
  };

  // Bands are typed as "from:percent" pairs, one per comma: 0:5, 100000:8 means 5% up to 1,00,000 and 8% above.
  const bandsOf = (text) => text.split(",").map((part) => part.trim()).filter(Boolean).map((part) => {
    const [from, pct] = part.split(":").map((v) => v.trim());
    return { from, rate: String(Number(pct) / 100) };
  });
  const add = async (event) => {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      await apiPost(`${PAY}/pay-commission-plans/`, { name: form.name, tiers: bandsOf(form.bands), cap: form.cap || null });
      setNotice({ tone: "success", text: "Plan added." });
      setForm({ name: "", bands: "0:5", cap: "" });
      await load();
    } catch (err) {
      setNotice({ tone: "danger", text: errorText(err, "That plan could not be saved.") });
    } finally {
      setBusy(false);
    }
  };
  const columns = [
    { key: "name", header: "Plan", className: "is-card-title", render: (p) => <span className="ds-strong">{p.name}</span> },
    { key: "bands", header: "Bands", render: (p) => p.tiers.map((t) => `from ₹${Number(t.from).toLocaleString("en-IN")}: ${percent(Number(t.rate))}`).join(" · ") },
    { key: "cap", header: "Most it pays", align: "right", render: (p) => (p.cap === null ? "No limit" : `₹${Number(p.cap).toLocaleString("en-IN")}`) },
    { key: "status", header: "Status", render: (p) => (p.verified ? <Badge variant="success">Checked</Badge> : <Badge variant="warning">Needs checking</Badge>) },
    { key: "act", header: "", align: "right", className: "is-card-actions", render: (p) => (p.verified ? null
      : <Button size="sm" disabled={busy} onClick={() => check(p)}>Mark as checked</Button>) },
  ];
  return (
    <Card title="Commission plans">
      <p className="ds-small">Each rate applies only to the part of the amount achieved inside its band. A plan is used only after someone other than its author has checked it.</p>
      {notice && <Alert tone={notice.tone} role="status">{notice.text}</Alert>}
      {error ? <Alert tone="danger">{error}</Alert> : !plans ? <EmptyState>Loading…</EmptyState> : (
        <Table stack flush columns={columns} rows={plans} empty="No commission plans yet." />
      )}
      <form className="Pay-Form" onSubmit={add}>
        <FormGrid>
          <Field label="Plan name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={120} /></Field>
          <Field label="Bands" hint="Amount where a band starts, then its rate in percent: 0:5, 100000:8">
            <Input value={form.bands} onChange={(e) => setForm({ ...form, bands: e.target.value })} required />
          </Field>
          <Field label="Most it pays (₹, optional)"><Input inputMode="decimal" value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} /></Field>
        </FormGrid>
        {notice && <Alert tone={notice.tone} role="status">{notice.text}</Alert>}
        <div className="Modal-Actions"><Button type="submit" variant="primary" disabled={busy || !form.name.trim()}>Add plan</Button></div>
      </form>
    </Card>
  );
}
